"use client";

import { useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import {
  Headphones,
  Loader2,
  Phone,
  PhoneOff,
  Mic,
  Clock,
  Zap,
} from "lucide-react";
import useMobile from "@/app/lib/features/mobile/mobile.hook";
import PageHero from "@/app/components/elements/PageHero";
import { Button } from "@/app/components/shadcn/button";
import useAiCall from "@/app/hooks/useAiCall";
import useLiveSupport, { SipConfig } from "@/app/hooks/useLiveSupport";
import useAuth from "@/app/lib/features/auth/auth.hook";
import { trackGeneric } from "@/app/lib/tracking/interactions";

const SUPPORT_SIP_CONFIG: SipConfig = {
  wssServer: "wss://callapi001.limcell.com:8089/ws",
  uri: "sip:3000@callapi001.limcell.com",
  username: "3000",
  password: "abcd1234!.X",
  target:
    "sip:enccaldMG0Ko0syW+0+MmDrDtiHhArzwjJYkm2Ev27G+EMzI5Y=@callapi001.limcell.com",
  iceServers: [
    { urls: "stun:stun.limcell.com:3478" },
    {
      urls: "turn:stun.limcell.com:3478",
      username: "limstunuser",
      credential: "limstunpass123",
    },
  ],
};

const AiDestekClient = () => {
  const router = useRouter();
  const { userProfile, isAuthenticated } = useAuth();

  useEffect(() => {
    // Auth henüz yükleniyorsa (cookie var ama profile gelmedi) bekle
    const hasToken = document.cookie.includes("access_token");
    if (!hasToken && !isAuthenticated) {
      router.replace("/auth/login?redirect=/canli-destek");
    }
  }, [isAuthenticated, router]);

  const displayName = userProfile
    ? `${userProfile.firstname} ${userProfile.lastname} - ${userProfile.phone}`
    : "Müşteri";

  const SUPPORT_SIP_CONFIG_WITH_NAME: SipConfig = {
    ...SUPPORT_SIP_CONFIG,
    displayName,
  };

  const {
    isConnected: aiConnected,
    callStatus: aiStatus,
    statusMessage: aiMessage,
    hangup: hangupAi,
    isLoadingConfig,
  } = useAiCall();

  const {
    isConnected: manualConnected,
    callStatus: manualStatus,
    statusMessage: manualMessage,
    makeCall: makeManualCall,
    hangup: hangupManual,
    open: openManual,
  } = useLiveSupport(SUPPORT_SIP_CONFIG_WITH_NAME);

  const { isMobileAi } = useMobile();

  const hasOpenedRef = useRef(false);

  useEffect(() => {
    if (hasOpenedRef.current) return;

    const hasToken =
      typeof document !== "undefined" &&
      document.cookie.includes("access_token");

    if (!hasToken) {
      // Guest — open immediately, no profile needed
      hasOpenedRef.current = true;
      openManual();
      return;
    }

    // Logged-in user — wait until userProfile is loaded so displayName is correct
    if (userProfile) {
      hasOpenedRef.current = true;
      openManual();
    }
  }, [userProfile, openManual]);

  // Aktif olan çağrıyı belirle
  const aiActive = ["calling", "active"].includes(aiStatus);
  const manualActive = ["calling", "active"].includes(manualStatus);

  // Manual akış mount olduğunda otomatik açılıyor (AI akışı şu an devre dışı),
  // bu yüzden idle dışındaki her durumda manual'ı göster
  const manualReady = manualStatus !== "idle";
  const activeStatus = manualReady ? manualStatus : aiStatus;
  const activeMessage = manualReady ? manualMessage : aiMessage;
  const isConnected = manualReady ? manualConnected : aiConnected;

  const isActive = activeStatus === "active";
  const isCalling = activeStatus === "calling";
  const isRegistered = activeStatus === "registered";
  const isError = activeStatus === "error";
  const isEnded = activeStatus === "ended";
  const isWaiting =
    !isRegistered && !isCalling && !isActive && !isError && !isEnded;

  const handleManualCall = () => {
    trackGeneric("live_support", "click");
    makeManualCall();
  };

  const handleHangup = () => {
    trackGeneric("live_support_hangup", "click");
    if (manualActive) hangupManual();
    else hangupAi();
  };

  const canCall = isRegistered || isEnded;
  const isInCall = isCalling || isActive;

  return (
    <div className="min-h-screen bg-white">
      {!isMobileAi && (
        <PageHero
          eyebrow="Tozlu.com · Canlı Destek"
          title="Canlı Destek"
          description="AI destekli veya insan temsilciyle sesli bağlantı kurun."
          imageUrl="https://images.unsplash.com/photo-1531746790731-6c087fecd65a?w=1600&q=80"
          breadcrumbs={[
            { title: "Ana Sayfa", url: "/" },
            { title: "Canlı Destek" },
          ]}
        />
      )}

      <div className="container-minify py-14">
        <div
          className={`grid grid-cols-1 gap-4 ${!isMobileAi ? "md:grid-cols-3" : ""}`}
        >
          {/* Ana arama kartı — 2 kolon */}
          <div
            className={`bg-gray-100 rounded-2xl p-7 flex flex-col justify-between min-h-64 ${!isMobileAi ? "md:col-span-2" : ""}`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center relative">
                  <Headphones className="w-5 h-5 text-gray-700" />
                  <span
                    className={`absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 border-gray-100 transition-colors duration-500 ${
                      isActive
                        ? "bg-green-400"
                        : isCalling
                          ? "bg-blue-400 animate-pulse"
                          : isRegistered
                            ? "bg-green-400"
                            : isError
                              ? "bg-red-400"
                              : "bg-gray-300"
                    }`}
                  />
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-widest">
                    {manualActive
                      ? "Müşteri Hizmetleri"
                      : "AI Müşteri Hizmetleri"}
                  </p>
                </div>
              </div>

              {isConnected && (
                <span className="text-xs font-medium text-green-600 bg-green-100 px-3 py-1 rounded-full">
                  Bağlı
                </span>
              )}
            </div>

            {/* Durum göstergesi */}
            <div className="flex flex-col items-center justify-center py-6 gap-3">
              {(isWaiting || isLoadingConfig) && (
                <>
                  <Loader2 size={26} className="text-gray-400 animate-spin" />
                  <p className="text-sm text-gray-400">{activeMessage}</p>
                </>
              )}

              {isRegistered && (
                <>
                  <div className="flex gap-1.5">
                    {[...Array(3)].map((_, i) => (
                      <span
                        key={i}
                        className="w-2 h-2 rounded-full bg-green-400"
                      />
                    ))}
                  </div>
                  <p className="text-sm font-medium text-green-600">
                    Temsilciye bağlanmaya hazır
                  </p>
                </>
              )}

              {isCalling && (
                <>
                  <div className="flex gap-1 items-end">
                    {[...Array(4)].map((_, i) => (
                      <div
                        key={i}
                        className="w-1.5 bg-blue-400 rounded-full animate-pulse"
                        style={{
                          height: `${14 + i * 5}px`,
                          animationDelay: `${i * 0.12}s`,
                        }}
                      />
                    ))}
                    {[...Array(4)].map((_, i) => (
                      <div
                        key={i + 4}
                        className="w-1.5 bg-blue-400 rounded-full animate-pulse"
                        style={{
                          height: `${29 - i * 5}px`,
                          animationDelay: `${(i + 4) * 0.12}s`,
                        }}
                      />
                    ))}
                  </div>
                  <p className="text-sm font-medium text-blue-600">
                    Temsilci aranıyor...
                  </p>
                </>
              )}

              {isActive && (
                <>
                  <div className="flex gap-1 items-center">
                    {[...Array(9)].map((_, i) => (
                      <div
                        key={i}
                        className="w-1.5 bg-green-500 rounded-full animate-pulse"
                        style={{
                          height: `${10 + Math.abs(4 - i) * 4}px`,
                          animationDelay: `${i * 0.1}s`,
                          animationDuration: `${0.6 + (i % 3) * 0.2}s`,
                        }}
                      />
                    ))}
                  </div>
                  <p className="text-sm font-medium text-green-600">
                    Görüşme devam ediyor
                  </p>
                </>
              )}

              {isEnded && (
                <>
                  <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
                    <PhoneOff size={18} className="text-gray-400" />
                  </div>
                  <p className="text-sm text-gray-500">Görüşme sona erdi</p>
                </>
              )}

              {isError && (
                <>
                  <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
                    <span className="text-lg">⚠️</span>
                  </div>
                  <p className="text-sm text-red-500">{activeMessage}</p>
                </>
              )}
            </div>

            {/* Butonlar */}
            <div className="space-y-2">
              {canCall && (
                <div className="flex gap-2">
                  {/* <Button
                    onClick={makeAiCall}
                    disabled={aiActive || manualActive}
                    className="flex-1 h-11 rounded-xl gap-2 text-sm font-medium"
                  >
                    <Phone size={15} />
                    AI Temsilci Ara
                  </Button> */}
                  <Button
                    onClick={handleManualCall}
                    disabled={aiActive || manualActive}
                    className="flex-1 h-11 rounded-xl gap-2 text-sm font-medium"
                  >
                    <Phone size={15} />
                    Temsilci Ara
                  </Button>
                </div>
              )}

              {isInCall && (
                <Button
                  onClick={handleHangup}
                  className="w-full h-11 bg-red-500 hover:bg-red-600 text-white rounded-xl gap-2 text-sm font-medium"
                >
                  <PhoneOff size={15} />
                  {isCalling ? "Aramayı İptal Et" : "Görüşmeyi Bitir"}
                </Button>
              )}

              {isError && (
                <div className="flex gap-2">
                  {/* <Button
                    onClick={makeAiCall}
                    className="flex-1 h-11 rounded-xl gap-2 text-sm font-medium"
                  >
                    <Phone size={15} />
                    AI Temsilci Ara
                  </Button> */}
                  <Button
                    onClick={handleManualCall}
                    className="flex-1 h-11 rounded-xl gap-2 text-sm font-medium"
                  >
                    <Phone size={15} />
                    Temsilci Ara
                  </Button>
                </div>
              )}

              <p className="text-xs text-gray-400 text-center pt-1">
                Bağlantı için mikrofon izni gereklidir
              </p>
            </div>
          </div>

          {/* Sağ kolon — 3 bilgi kartı */}
          {!isMobileAi && (
            <div className="flex flex-col gap-4">
              <div className="bg-gray-100 rounded-2xl p-7 flex flex-col justify-between flex-1">
                <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center mb-4">
                  <Zap className="w-5 h-5 text-gray-700" />
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-widest mb-1">
                    Yanıt Süresi
                  </p>
                  <p className="text-2xl font-bold text-gray-900">Anında</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Bekleme süresi yok
                  </p>
                </div>
              </div>

              <div className="bg-gray-100 rounded-2xl p-7 flex flex-col justify-between flex-1">
                <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center mb-4">
                  <Clock className="w-5 h-5 text-gray-700" />
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-widest mb-1">
                    Hizmet Saati
                  </p>
                  <p className="text-2xl font-bold text-gray-900">7/24</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Kesintisiz destek
                  </p>
                </div>
              </div>

              <div className="bg-gray-100 rounded-2xl p-7 flex flex-col justify-between flex-1">
                <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center mb-4">
                  <Mic className="w-5 h-5 text-gray-700" />
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-widest mb-1">
                    Teknoloji
                  </p>
                  <p className="text-2xl font-bold text-gray-900">AI + Canlı</p>
                  <p className="text-xs text-gray-400 mt-1">
                    İki seçenekli destek
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AiDestekClient;
